// angular
import {Component, OnInit, inject} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
// components
import {SelectComponent} from "@app/core/components/select/select.component";
// material angular
import {MatButtonModule} from "@angular/material/button";
import {MatCardModule} from "@angular/material/card";
// services
import {TicketManagementService} from "@app/+ticket-management/shared/services/ticket-management.service";
import {EmployeeService} from "@app/shared/services/employee.service";
// types
import {SelectItemS} from "@app/core/components/select/Item.type";
import {IEmploysPeople} from "@app/+messaging/shared/interfaces/member.interface";

export interface AssignTicketDialogData {
  ticketId: string;
  currentEmployeeId?: string;
}

export interface AssignTicketDialogResponse {
  employeeId: string;
}

@Component({
  selector: "app-assign-ticket-dialog",
  templateUrl: "./assign-ticket-dialog.component.html",
  styleUrls: ["./assign-ticket-dialog.component.scss"],
  standalone: true,
  imports: [
    MatCardModule,
    TranslateModule,
    SelectComponent,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
  ],
})
export class AssignTicketDialogComponent implements OnInit {
  public form: FormGroup;
  public employeesSelectOptions: SelectItemS = [];
  public isLoading = false;

  // dependencies
  private _dialogRef =
    inject<
      MatDialogRef<AssignTicketDialogComponent, AssignTicketDialogResponse>
    >(MatDialogRef);
  private _data = inject<AssignTicketDialogData>(MAT_DIALOG_DATA);
  private _ticketService = inject(TicketManagementService);
  private _employeeService = inject(EmployeeService);

  ngOnInit() {
    this._buildForm();
    this._loadEmployees();
  }

  private _buildForm(): void {
    this.form = new FormGroup({
      employee: new FormControl(
        this._data.currentEmployeeId || null,
        Validators.required
      ),
    });
  }

  private _loadEmployees(): void {
    this.isLoading = true;
    this._employeeService.getEmployees().subscribe({
      next: (employees) => {
        this.employeesSelectOptions = employees.map(
          (employee: IEmploysPeople) => ({
            value: employee?.employee?.id,
            key: `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim(),
          })
        );
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const employeeId = this.form.value.employee;

    this._ticketService.assignToEmployee(this._data.ticketId, {
      employee_id: employeeId,
    });

    this._dialogRef.close({employeeId});
  }
}
